import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit, Trash2, Building2, ChevronLeft, ChevronRight, X, Package, Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import api from '@/services/api';
import { PermissionGate } from '@/components/PermissionGate';
import { useToast } from '@/hooks/useToast';

interface Branch {
  id: string;
  name: string;
  code: string;
  address?: string;
  phone?: string;
  isActive: boolean;
  createdAt: string;
  _count?: {
    assets: number;
    employees: number;
    departments: number;
    locations: number;
  };
}

interface BranchFormData {
  name: string;
  code: string;
  address: string;
  phone: string;
  isActive: boolean;
}

const initialFormData: BranchFormData = {
  name: '',
  code: '',
  address: '',
  phone: '',
  isActive: true,
};

export default function BranchesPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [formData, setFormData] = useState<BranchFormData>(initialFormData);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportingBranch, setExportingBranch] = useState<Branch | null>(null);
  const [exporting, setExporting] = useState<string | null>(null);

  // Fetch branches
  const { data, isLoading } = useQuery({
    queryKey: ['branches', { page, search }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      if (search) params.append('search', search);
      const response = await api.get(`/branches?${params.toString()}`);
      return response.data;
    },
  });

  const branches: Branch[] = data?.data || [];
  const pagination = data?.pagination;

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: BranchFormData) => {
      return api.post('/branches', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      closeModal();
      toast({ title: 'Success', description: 'Branch created successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to create branch', variant: 'destructive' });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: BranchFormData }) => {
      return api.put(`/branches/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      closeModal();
      toast({ title: 'Success', description: 'Branch updated successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to update branch', variant: 'destructive' });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/branches/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      setShowDeleteConfirm(false);
      setDeletingId(null);
      toast({ title: 'Success', description: 'Branch deleted successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to delete branch', variant: 'destructive' });
    },
  });

  const openCreateModal = () => {
    setEditingBranch(null);
    setFormData(initialFormData);
    setShowModal(true);
  };

  const openEditModal = (branch: Branch) => {
    setEditingBranch(branch);
    setFormData({
      name: branch.name,
      code: branch.code,
      address: branch.address || '',
      phone: branch.phone || '',
      isActive: branch.isActive,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBranch(null);
    setFormData(initialFormData);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingBranch) {
      updateMutation.mutate({ id: editingBranch.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const confirmDelete = (id: string) => {
    setDeletingId(id);
    setShowDeleteConfirm(true);
  };

  const openExportModal = (branch: Branch) => {
    setExportingBranch(branch);
    setShowExportModal(true);
  };

  const handleExport = async (format: 'csv' | 'xlsx' | 'pdf') => {
    if (!exportingBranch) return;
    
    setExporting(format);
    try {
      const response = await api.get(`/branches/${exportingBranch.id}/assets/export?format=${format}`, {
        responseType: 'blob',
      });
      
      const contentType = String(response.headers['content-type'] ?? '') || undefined;
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const ext = format === 'xlsx' ? 'xlsx' : format === 'pdf' ? 'pdf' : 'csv';
      link.download = `${exportingBranch.name.replace(/[^a-zA-Z0-9]/g, '-')}-assets-${new Date().toISOString().split('T')[0]}.${ext}`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast({ title: 'Success', description: `Assets exported as ${format.toUpperCase()}` });
      setShowExportModal(false);
      setExportingBranch(null);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to export assets', variant: 'destructive' });
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Branches</h1>
          <p className="text-muted-foreground">Manage organization branches and locations</p>
        </div>
        <PermissionGate permission="branches.create">
          <Button onClick={openCreateModal}>
            <Plus className="w-4 h-4 mr-2" />
            Add Branch
          </Button>
        </PermissionGate>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search branches..."
                className="pl-10"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : branches.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No branches found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium">Name</th>
                    <th className="text-left p-4 font-medium">Code</th>
                    <th className="text-left p-4 font-medium">Address</th>
                    <th className="text-left p-4 font-medium">Phone</th>
                    <th className="text-center p-4 font-medium">Assets</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-right p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {branches.map((branch) => (
                    <tr key={branch.id} className="hover:bg-muted/50">
                      <td className="p-4 font-medium">{branch.name}</td>
                      <td className="p-4">
                        <span className="font-mono text-sm bg-muted px-2 py-1 rounded">
                          {branch.code}
                        </span>
                      </td>
                      <td className="p-4 text-muted-foreground">{branch.address || '-'}</td>
                      <td className="p-4 text-muted-foreground">{branch.phone || '-'}</td>
                      <td className="p-4 text-center">
                        <button
                          onClick={() => openExportModal(branch)}
                          className={cn(
                            "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors",
                            (branch._count?.assets || 0) > 0
                              ? "bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer"
                              : "bg-gray-100 text-gray-500"
                          )}
                          disabled={(branch._count?.assets || 0) === 0}
                        >
                          <Package className="w-3.5 h-3.5" />
                          {branch._count?.assets || 0}
                        </button>
                      </td>
                      <td className="p-4">
                        <span className={cn(
                          "px-2 py-1 text-xs font-medium rounded-full",
                          branch.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                        )}>
                          {branch.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-1">
                          <PermissionGate permission="branches.update">
                            <Button variant="ghost" size="icon" onClick={() => openEditModal(branch)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate permission="branches.delete">
                            <Button variant="ghost" size="icon" onClick={() => confirmDelete(branch.id)}>
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-sm text-muted-foreground">
              Showing {((page - 1) * pagination.limit) + 1} to {Math.min(page * pagination.limit, pagination.total)} of {pagination.total}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" disabled={page === 1} onClick={() => setPage(page - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm">Page {page} of {pagination.totalPages}</span>
              <Button variant="outline" size="icon" disabled={page === pagination.totalPages} onClick={() => setPage(page + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">
                  {editingBranch ? 'Edit Branch' : 'Create Branch'}
                </h2>
                <Button variant="ghost" size="icon" onClick={closeModal}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="code">Code *</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    required
                    placeholder="e.g., HQ, BR01"
                  />
                </div>
                <div>
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-4 h-4 rounded border-input"
                  />
                  <Label htmlFor="isActive">Active</Label>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={closeModal}>
                    Cancel
                  </Button>
                  <Button type="submit" loading={createMutation.isPending || updateMutation.isPending}>
                    {editingBranch ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-2">Confirm Deletion</h3>
              <p className="text-muted-foreground mb-6">
                Are you sure you want to delete this branch? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => deletingId && deleteMutation.mutate(deletingId)}
                  loading={deleteMutation.isPending}
                >
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Export Assets Modal */}
      {showExportModal && exportingBranch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold">Export Assets</h2>
                  <p className="text-sm text-muted-foreground">{exportingBranch.name} - {exportingBranch._count?.assets || 0} assets</p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => { setShowExportModal(false); setExportingBranch(null); }}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground mb-4">
                  Choose a format to export all assets from this branch:
                </p>
                
                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 h-14"
                  onClick={() => handleExport('csv')}
                  disabled={!!exporting}
                >
                  {exporting === 'csv' ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <FileText className="w-5 h-5 text-green-600" />
                  )}
                  <div className="text-left">
                    <div className="font-medium">CSV File</div>
                    <div className="text-xs text-muted-foreground">Comma-separated values, opens in Excel</div>
                  </div>
                </Button>
                
                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 h-14"
                  onClick={() => handleExport('xlsx')}
                  disabled={!!exporting}
                >
                  {exporting === 'xlsx' ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="w-5 h-5 text-green-700" />
                  )}
                  <div className="text-left">
                    <div className="font-medium">Excel File</div>
                    <div className="text-xs text-muted-foreground">Microsoft Excel spreadsheet (.xlsx)</div>
                  </div>
                </Button>
                
                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 h-14"
                  onClick={() => handleExport('pdf')}
                  disabled={!!exporting}
                >
                  {exporting === 'pdf' ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Download className="w-5 h-5 text-red-600" />
                  )}
                  <div className="text-left">
                    <div className="font-medium">PDF Report</div>
                    <div className="text-xs text-muted-foreground">Printable document format</div>
                  </div>
                </Button>
              </div>

              <div className="mt-6 pt-4 border-t">
                <Button
                  variant="link"
                  className="w-full text-sm"
                  onClick={() => { 
                    setShowExportModal(false); 
                    setExportingBranch(null);
                    navigate(`/assets?branchId=${exportingBranch.id}`);
                  }}
                >
                  Or view assets in browser →
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
